import React, { useState, FormEvent, useEffect } from "react";
import { Container, Form, Button, Card, Tabs, Tab } from "react-bootstrap";
import { Heading, Summary, LevelCounts, TabName, HeadingLevel } from "./types";
import {
  FaGlobe,
  FaSearch,
  FaSpinner,
  FaListAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
  FaCode,
} from "react-icons/fa";
import { Box } from "./headingTagsStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const HeadingTagsChecker: React.FC = () => {
  const [url, setUrl] = useState<string>("");
  const [htmlInput, setHtmlInput] = useState<string>("");
  const [inputMode, setInputMode] = useState<"url" | "html">("url");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [summary, setSummary] = useState<Summary>({
    total: 0,
    success: 0,
    warnings: 0,
    errors: 0,
  });
  const [levelCounts, setLevelCounts] = useState<LevelCounts>({
    h1: 0,
    h2: 0,
    h3: 0,
    h4: 0,
    h5: 0,
    h6: 0,
  });
  const [activeTab, setActiveTab] = useState<TabName>("all");

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [inputMode, activeTab, error, headings]);

  const validateHeadingStructure = (headings: Heading[]): Heading[] => {
    let previousLevel = 0;
    return headings.map((heading) => {
      const issues: string[] = [];

      if (!heading.text.trim()) {
        issues.push("Empty heading detected");
      }

      if (heading.level > previousLevel + 1) {
        issues.push(
          `Incorrect heading hierarchy - should be h${previousLevel + 1}`
        );
      }

      if (heading.text.length > 60) {
        issues.push("Heading is too long (recommended: max 60 characters)");
      }

      const duplicates = headings.filter(
        (h) => h.text === heading.text && h.level === heading.level
      );
      if (duplicates.length > 1) {
        issues.push("Duplicate heading detected");
      }

      previousLevel = heading.level;

      const status =
        issues.length === 0
          ? "success"
          : issues.some(
              (issue) => issue.includes("hierarchy") || issue.includes("Empty")
            )
          ? "error"
          : "warning";

      return { ...heading, issues, status };
    });
  };

  const cleanUrl = (url: string): string => {
    url = url.trim();
    url = url.replace(/^https?:\/\//, "");
    url = url.replace(/^www\./, "");
    url = url.replace(/\/+$/, "");
    return url;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(`https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const analyzeHeadingsFromHtml = (html: string) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const headings: Heading[] = [];
      for (let i = 1; i <= 6; i++) {
        doc.querySelectorAll(`h${i}`).forEach((element) => {
          headings.push({
            text: element.textContent?.trim() || "",
            level: i,
            issues: [],
            status: "success",
          });
        });
      }

      const analyzedHeadings = validateHeadingStructure(headings);

      const summary: Summary = {
        total: analyzedHeadings.length,
        success: analyzedHeadings.filter((h) => h.status === "success").length,
        warnings: analyzedHeadings.filter((h) => h.status === "warning").length,
        errors: analyzedHeadings.filter((h) => h.status === "error").length,
      };

      const levelCounts: LevelCounts = {
        h1: 0,
        h2: 0,
        h3: 0,
        h4: 0,
        h5: 0,
        h6: 0,
      };
      analyzedHeadings.forEach((heading) => {
        const key = `h${heading.level}` as HeadingLevel;
        levelCounts[key]++;
      });

      setHeadings(analyzedHeadings);
      setSummary(summary);
      setLevelCounts(levelCounts);
      setError("");
    } catch (err: any) {
      setError(
        "Failed to parse the HTML. Please ensure it is valid HTML and try again."
      );
      setHeadings([]);
      setSummary({ total: 0, success: 0, warnings: 0, errors: 0 });
      setLevelCounts({ h1: 0, h2: 0, h3: 0, h4: 0, h5: 0, h6: 0 });
    }
  };

  const fetchWithRetry = async (
    url: string,
    retries: number = 3,
    delay: number = 1000
  ): Promise<string> => {
    const apiUrl = `https://api.performancedashboard.io/api/page-content?url=${encodeURIComponent(
      url
    )}`;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await fetch(apiUrl, {
          headers: {
            "User-Agent":
              "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
          },
        });

        if (!response.ok) {
          throw new Error(`Fetch failed with status ${response.status}`);
        }

        const jsonResponse = await response.json();
        if (!jsonResponse.success || !jsonResponse.data) {
          throw new Error(
            "Invalid response from API: Missing success or data field"
          );
        }

        return jsonResponse.data;
      } catch (err) {
        if (attempt === retries - 1) {
          throw err;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    throw new Error("All fetch attempts failed");
  };

  const analyzeHeadingsFromUrl = async (url: string) => {
    try {
      const html = await fetchWithRetry(url);
      analyzeHeadingsFromHtml(html);
    } catch (err: any) {
      let errorMessage = "Failed to analyze the URL. Please try again.";
      if (err.message.includes("Fetch failed")) {
        errorMessage =
          "Unable to fetch the webpage. The site may be blocking requests or is inaccessible.";
      } else if (err.message.includes("Invalid response from API")) {
        errorMessage =
          "Invalid response from the API. Please ensure the URL is correct and try again.";
      } else if (err.message.includes("network")) {
        errorMessage =
          "Network error occurred. Please check your internet connection and try again.";
      }

      errorMessage +=
        '\nSuggestions:\n- Ensure the URL is correct and the website is accessible.\n- Try a different website.\n- Retry after some time.\n- Alternatively, paste the HTML code directly using the "Paste HTML" option.';

      setError(errorMessage);
      setHeadings([]);
      setSummary({ total: 0, success: 0, warnings: 0, errors: 0 });
      setLevelCounts({ h1: 0, h2: 0, h3: 0, h4: 0, h5: 0, h6: 0 });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (inputMode === "url") {
      if (!url.trim()) {
        setError("Please enter a URL");
        return;
      }

      const cleanedUrl = cleanUrl(url);
      const fullUrl = `https://${cleanedUrl}`;

      if (!isValidUrl(cleanedUrl)) {
        setError("Please enter a valid URL (e.g., example.com)");
        return;
      }

      setIsLoading(true);
      await analyzeHeadingsFromUrl(fullUrl);
      setIsLoading(false);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } else {
      if (!htmlInput.trim()) {
        setError("Please paste HTML code to analyze");
        return;
      }

      setIsLoading(true);
      analyzeHeadingsFromHtml(htmlInput);
      setIsLoading(false);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    }
  };

  const renderHeadingItem = (heading: Heading): React.ReactElement => {
    const statusIcon =
      heading.status === "success" ? (
        <FaCheckCircle className="htc-status-icon htc-success" />
      ) : heading.status === "warning" ? (
        <FaExclamationCircle className="htc-status-icon htc-warning" />
      ) : (
        <FaTimesCircle className="htc-status-icon htc-error" />
      );

    return (
      <div
        className="htc-heading-item"
        key={`${heading.level}-${heading.text}`}
      >
        {statusIcon}
        <div className="htc-heading-content">
          <div className="htc-heading-title">
            <span className="htc-heading-level">h{heading.level}</span>
            <span className="htc-heading-text">
              {heading.text.trim() || (
                <em className="htc-empty-heading">Empty heading</em>
              )}
            </span>
          </div>
          {heading.issues.length > 0 && (
            <ul className="htc-issues-list">
              {heading.issues.map((issue, index) => (
                <li
                  key={index}
                  className={`htc-issue ${
                    heading.status === "warning" ? "htc-warning" : ""
                  }`}
                >
                  {issue}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderTabContent = (tab: TabName): React.ReactNode => {
    const filteredHeadings =
      tab === "all"
        ? headings
        : headings.filter((h) => h.level === parseInt(tab.charAt(1)));
    if (filteredHeadings.length === 0) {
      return (
        <div className="htc-empty-tab-message">
          No {tab === "all" ? "" : tab.toUpperCase()} headings found on this
          page.
        </div>
      );
    }
    return filteredHeadings.map(renderHeadingItem);
  };

  return (
    <Box>
      <Container
        className={`htc-container ${
          !isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
        }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="Heading Tags Checker"
            description="Analyze heading tags to enhance SEO and strengthen content structure."
          />
        )}
        <div className="htc-input-mode-toggle mb-4" style={{ paddingTop: !isInIframe ? "0" : "20px" }}>
  <Button
    onClick={() => setInputMode("url")}
    className={`htc-toggle-btn me-2 ${inputMode === "url" ? "htc-toggle-active" : ""}`}
  >
    <FaGlobe className="me-2" /> Enter URL
  </Button>

  <Button
    onClick={() => setInputMode("html")}
    className={`htc-toggle-btn ${inputMode === "html" ? "htc-toggle-active" : ""}`}
  >
    <FaCode className="me-2" /> Paste HTML
  </Button>
</div>


        <Form onSubmit={handleSubmit} className="htc-url-form mb-5">
          {inputMode === "url" ? (
            <div className="htc-input-group">
              <div className="htc-url-input-wrapper">
                <Form.Control
                  type="text"
                  id="htcUrlInput"
                  placeholder="Enter website URL (e.g., example.com)"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required={inputMode === "url"}
                  className="htc-custom-input"
                />
                <div className="htc-url-hint">
                  No need to add https:// or www
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="htc-btn htc-btn-primary"
              >
                {isLoading ? (
                  <FaSpinner className="htc-spinner" />
                ) : (
                  <span className="htc-button-content">
                    <FaSearch className="htc-search-icon me-2" />
                    Scan Page
                  </span>
                )}
              </Button>
            </div>
          ) : (
            <div className="htc-html-input-wrapper">
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Paste your HTML code here..."
                value={htmlInput}
                onChange={(e) => setHtmlInput(e.target.value)}
                required={inputMode === "html"}
                className="htc-custom-textarea"
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="htc-btn htc-btn-primary mt-3"
              >
                {isLoading ? (
                  <FaSpinner className="htc-spinner" />
                ) : (
                  <span className="htc-button-content">
                    <FaCode className="htc-search-icon me-2" />
                    Analyze HTML
                  </span>
                )}
              </Button>
            </div>
          )}
        </Form>

        {error && (
          <div className="htc-error p-3 mb-4 rounded">
            {error}
          </div>
        )}

        {headings.length > 0 && (
          <div className="htc-results">
            <div className="htc-summary-grid">
              <Card className="htc-summary-card">
                <Card.Body className="text-center">
                  <FaListAlt className="htc-card-icon htc-total mb-3" />
                  <div className="htc-number htc-total">{summary.total}</div>
                  <div className="htc-label">Total Headings</div>
                </Card.Body>
              </Card>
              <Card className="htc-summary-card htc-success">
                <Card.Body className="text-center">
                  <FaCheckCircle className="htc-card-icon htc-success mb-3" />
                  <div className="htc-number htc-success">
                    {summary.success}
                  </div>
                  <div className="htc-label">Optimized</div>
                </Card.Body>
              </Card>
              <Card className="htc-summary-card htc-warning">
                <Card.Body className="text-center">
                  <FaExclamationCircle className="htc-card-icon htc-warning mb-3" />
                  <div className="htc-number htc-warning">
                    {summary.warnings}
                  </div>
                  <div className="htc-label">Warnings</div>
                </Card.Body>
              </Card>
              <Card className="htc-summary-card htc-error">
                <Card.Body className="text-center">
                  <FaTimesCircle className="htc-card-icon htc-error mb-3" />
                  <div className="htc-number htc-error">{summary.errors}</div>
                  <div className="htc-label">Errors</div>
                </Card.Body>
              </Card>
            </div>

            <div className="htc-heading-levels-overview mb-4">
              <h2 className="htc-fs-5">Heading Levels Overview</h2>
              <div className="htc-table-wrapper">
                <table className="htc-heading-levels-table">
                  <thead>
                    <tr>
                      <th>H1</th>
                      <th>H2</th>
                      <th>H3</th>
                      <th>H4</th>
                      <th>H5</th>
                      <th>H6</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{levelCounts.h1}</td>
                      <td>{levelCounts.h2}</td>
                      <td>{levelCounts.h3}</td>
                      <td>{levelCounts.h4}</td>
                      <td>{levelCounts.h5}</td>
                      <td>{levelCounts.h6}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k as TabName)}
              className="htc-tabs-container"
              variant="tabs"
            >
              <Tab
                eventKey="all"
                title="All Headings"
                className="htc-tab-content"
              >
                {renderTabContent("all")}
              </Tab>
              <Tab eventKey="h1" title="H1" className="htc-tab-content">
                {renderTabContent("h1")}
              </Tab>
              <Tab eventKey="h2" title="H2" className="htc-tab-content">
                {renderTabContent("h2")}
              </Tab>
              <Tab eventKey="h3" title="H3" className="htc-tab-content">
                {renderTabContent("h3")}
              </Tab>
              <Tab eventKey="h4" title="H4" className="htc-tab-content">
                {renderTabContent("h4")}
              </Tab>
              <Tab eventKey="h5" title="H5" className="htc-tab-content">
                {renderTabContent("h5")}
              </Tab>
              <Tab eventKey="h6" title="H6" className="htc-tab-content">
                {renderTabContent("h6")}
              </Tab>
            </Tabs>
          </div>
        )}
      </Container>
    </Box>
  );
};

export default HeadingTagsChecker;